import {
  Table as TableMaterial,
  TableContainer,
  TableBody,
  TableRow,
  TablePagination,
  TableHead,
  TableSortLabel,
} from '@mui/material';
import { memo, useCallback } from 'react';
import { BaseTableRows, HeadCell } from 'shell/components/shared/table/types';
import { OrderTypes } from 'shell/constants/order-types';
import { Order } from 'shell/view-models/order';
import DotMenu from './dot-menu';
import { requestSort } from 'shell/utils/request-sort';
import { useOrder } from 'shell/hooks/use-order';
import { PmiTableRow } from 'shell/components/shared/table/row';
import { ActiveTableActions } from 'system/components/shared/list-item-cta-menu/types';
import { PmiHeadTableCell } from 'shell/components/shared/table-custom/head';
import { PmiActionTableCell, PmiBodyTableCell } from 'shell/components/shared/table-custom/body';

interface TableProps<T extends BaseTableRows, K extends keyof T> {
  head: Array<HeadCell<T, K>>;
  count: number;
  body: Array<T>;
  page?: number;
  isEdit?: boolean;
  rowsPerPage?: number;
  orders?: Order;
  ariaLabel: string;
  onDetailPress?: (cell: T, page?: number) => Promise<T> | void;
  onEditPress?: (cell: T, page?: number) => Promise<T> | void;
  onSort?: (orders?: Order) => void;
  onChangePage: (page?: number) => void;
  onChangeRowsPerPage?: (rowsPerPage?: number) => void;
  activeActions: ActiveTableActions;
}

const Table = <T extends BaseTableRows, K extends keyof T>({
  body = [],
  head,
  count,
  isEdit,
  page = 0,
  rowsPerPage = 10,
  onChangePage,
  onChangeRowsPerPage = () => {
    return;
  },
  onDetailPress = (cell) => {
    return Promise.resolve(cell);
  },
  onEditPress = (cell) => {
    return Promise.resolve(cell);
  },
  onSort = () => {
    return;
  },
  orders,
  ariaLabel,
  activeActions = {
    read: true,
    edit: true,
  },
}: TableProps<T, K>): React.ReactElement => {
  const existOrderFunction = useOrder<T, K>(orders || {});

  const handleRequestSort = useCallback(
    (property: K) => {
      const newOrders = requestSort(property, orders);
      onSort(newOrders);
    },
    [orders]
  );

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      onChangePage(newPage);
    },
    [onChangePage]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeRowsPerPage(parseInt(event.target.value, 10));
    },
    [onChangeRowsPerPage]
  );

  const firstItem = page * rowsPerPage;
  const lastItem = firstItem + rowsPerPage;

  return (
    <div className="pmi-mt-2">
      <TableContainer>
        <TableMaterial
          sx={{ minWidth: 950 }}
          size="medium"
          stickyHeader
          aria-label={ariaLabel}
          style={{ tableLayout: 'fixed' }}
        >
          <TableHead>
            <TableRow>
              {head.map((headCell) => {
                const { exist: existOrder, value } = existOrderFunction(headCell);
                return headCell.label ? (
                  <PmiHeadTableCell
                    key={headCell.id.toString()}
                    align="left"
                    sortDirection={!!existOrder ? value || OrderTypes.Asc : false}
                  >
                    <TableSortLabel
                      active={false}
                      disabled
                      direction={!!existOrder ? value : OrderTypes.Asc}
                      onClick={() => handleRequestSort(headCell.id as K)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </PmiHeadTableCell>
                ) : (
                  <PmiHeadTableCell key={headCell.id.toString()} align="left">
                    Azioni
                  </PmiHeadTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {body.slice(firstItem, lastItem).map((row, bodyItemIndex: number) => {
              const id = `table-row-${bodyItemIndex}`;

              return (
                <PmiTableRow
                  key={id}
                  onClick={() => {
                    if (activeActions.read) {
                      onDetailPress(row);
                    }
                  }}
                  tabIndex={0}
                >
                  {head.map((column, headItemIndex) => {
                    const labelId = `table-${row[column.id]}-${headItemIndex}`;
                    if (column.label) {
                      return (
                        <PmiBodyTableCell key={labelId} id={labelId} scope="row">
                          {row[column.id]}
                        </PmiBodyTableCell>
                      );
                    }
                    return null;
                  })}
                  <PmiActionTableCell>
                    <DotMenu
                      isEdit={isEdit}
                      active={activeActions}
                      onDetailPress={() => onDetailPress(row)}
                      onEditPress={() => onEditPress(row)}
                    />
                  </PmiActionTableCell>
                </PmiTableRow>
              );
            })}
          </TableBody>
        </TableMaterial>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        labelRowsPerPage="Righe per pagina:"
        count={count || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      ></TablePagination>
    </div>
  );
};

Table.displayName = 'Table component';

const memoizedTable = memo(Table);
export default memoizedTable as typeof Table;
